import { DateTime, Duration } from 'luxon';
import {
  ExportInterfaceConfigurationFileFormat,
  ExportInterfaceConfigurationDataSelectionType,
  ExportInterfaceConfigurationDetailsFileFilterBrandSubDTO,
  ExportInterfaceConfigurationDetailsFileLineBasedAllAttributesDTO,
  ExportInterfaceConfigurationDetailsSFTPDTO,
  ExportInterfaceConfigurationFileProcessType,
  ExportInterfaceConfigurationFinanceDataSelectionType,
  ExportInterfaceConfigurationFinanceSchedule,
  ExportInterfaceConfigurationType,
  ExportInterfaceConfigurationUserAccessInfoDTO,
  ExportInterfaceFieldAssignmentDTO,
  IExportInterfaceConfigurationDetailsFileFilterBrandSubDTO,
  IExportInterfaceFieldAssignmentDTO,
  UpdateConfigurationFromTemplateDTO,
  ExportInterfaceConfigurationFTPTestResultDTO,
  ExportInterfaceConfigurationClearingItemFilter,
  ExportInterfaceConfigurationMode,
  ExportInterfaceConfigurationAdditionalStockLocation,
} from '../../../../base/models/models.swagger.generated';
import { Injectable, OnDestroy } from '@angular/core';
import {
  ExportInterfaceConfigurationDetailsDTO,
  ExportInterfaceConfigurationDetailsEmailDTO,
  ExportInterfaceConfigurationDetailsFileDTO,
  ExportInterfaceConfigurationDetailsFileFilterDTO,
  ExportInterfaceConfigurationDetailsFTPClientDTO,
  ExportInterfaceConfigurationDetailsRESTAPIDTO,
  ExportInterfaceConfigurationDetailsRESTBTDTO,
  ExportInterfaceConfigurationDetailsRESTDTO,
  ExportInterfaceConfigurationDetailsTimeScheduleDTO,
  ExportInterfaceConfigurationRESTAuthType,
  ExportInterfaceConfigurationTransferType,
  ExportInterfaceFieldAssignmentsDTO,
} from 'src/app/base/models/models.swagger.generated';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { SpinnerService } from 'src/app/base/spinner/spinner.service';
import { ToastService } from 'src/app/base/services/toast.service';
import { ErrorType } from 'src/app/core/routing/error-types';
import { of, Subject, Subscription } from 'rxjs';
import { ConfigurationsService } from '../../configurations/configurations.service';
import { ConfigurationFormTimeplanValidators } from './configuration-form-timeplan/configuration-form-timeplan.validators';
import { ConfigurationTemplatesService } from '../../configuration-templates/configuration-templates.service';
import { PasswordGenerator } from 'src/app/base/shared/password-generator';

@Injectable()

export class ConfigurationFormCoordinatorService implements OnDestroy {

  public configurationDetailsDTO: ExportInterfaceConfigurationDetailsDTO;
  public configurationDetailsDTOUpdated: Subject<ExportInterfaceConfigurationDetailsDTO> = new Subject();
  public transferTypeChanged = new Subject<ExportInterfaceConfigurationTransferType>();
  public transferType: ExportInterfaceConfigurationTransferType;
  public fileProcessTypeChanged = new Subject<ExportInterfaceConfigurationFileProcessType>();
  public fileProcessType: ExportInterfaceConfigurationFileProcessType;
  public typeChanged = new Subject<ExportInterfaceConfigurationType>();
  public configurationType: ExportInterfaceConfigurationType;

  public configurationFormGeneral: UntypedFormGroup;
  public configurationFormTimeplan: UntypedFormGroup;
  public configurationFormREST: UntypedFormGroup;
  public configurationFormFTP: UntypedFormGroup;
  public configurationFormSFTP: UntypedFormGroup;
  public configurationFormFileName: UntypedFormGroup;
  public configurationFormFileFields: UntypedFormGroup;
  public configurationFormFileLineBasedAllAttributes: UntypedFormGroup;
  public configurationFormFileFilter: UntypedFormGroup;
  public configurationFormEmail: UntypedFormGroup;
  public configurationFormBacklog: UntypedFormGroup;
  public configurationFormFinance: UntypedFormGroup;

  public isWholeFormValid = false;

  public isTemplateMode = false;

  // cached data
  public testFileName: string = null;
  public ftpAccess: ExportInterfaceConfigurationFTPTestResultDTO;

  private _subscription: Subscription = new Subscription();

  constructor(
    private configurationsService: ConfigurationsService,
    private configurationTemplateService: ConfigurationTemplatesService,
    private spinnerService: SpinnerService,
    private toastService: ToastService,
    private fb: UntypedFormBuilder,
  ) {
    this.initializeForms();
  }

  ngOnDestroy() {
    if (this._subscription) {
      this._subscription.unsubscribe();
    }
  }

  public loadConfigurationDetails(id: number) {
    if (!this.isTemplateMode) {
      this.configurationsService.getConfigurationDetails(id).subscribe({next: (configurationDetailsDto: ExportInterfaceConfigurationDetailsDTO) => {
        this.spinnerService.hide();
        this.configurationDetailsDTO = configurationDetailsDto;
        this.transferType = configurationDetailsDto.transferType;
        this.transferTypeChanged.next(this.transferType);
        this.fileProcessType = configurationDetailsDto.fileDetails.processType;
        this.configurationsService.configurationType = configurationDetailsDto.type;
        this.typeChanged.next(configurationDetailsDto.type);
        this.configurationType = configurationDetailsDto.type;
        this.loadFormsData();
        this.testFileName = null;
        this.ftpAccess = null;
      }, error: (error: any) => {
        this.spinnerService.hide();
        if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
          this.toastService.showError('i18n.errors.server-error.title',
            'i18n.errors.server-error.message', null, ErrorType.serverError);
        }
        return of(error);
      }});
    }
    if (this.isTemplateMode) {
      this.configurationTemplateService.getConfigurationDetails(id).subscribe({next: (configurationDetailsDto: ExportInterfaceConfigurationDetailsDTO) => {
        this.spinnerService.hide();
        this.configurationDetailsDTO = configurationDetailsDto;
        this.transferType = configurationDetailsDto.transferType;
        this.transferTypeChanged.next(this.transferType);
        this.typeChanged.next(configurationDetailsDto.type);
        this.configurationType = configurationDetailsDto.type;
        this.configurationsService.configurationType = configurationDetailsDto.type;
        this.loadFormsData();
        this.testFileName = null;
        this.ftpAccess = null;
      }, error: (error: any) => {
        this.spinnerService.hide();
        if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
          this.toastService.showError('i18n.errors.server-error.title',
            'i18n.errors.server-error.message', null, ErrorType.serverError);
        }
        return of(error);
      }});
    }
  }

  public loadFromTemplate(templateConfigurationDetailDto: ExportInterfaceConfigurationDetailsDTO) {
    let updateConfiguration = new UpdateConfigurationFromTemplateDTO({
      clientId: this.configurationDetailsDTO.clientId,
      configurationId: this.configurationDetailsDTO.id,
      templateId: templateConfigurationDetailDto.id,
      importConfigurationId: this.configurationDetailsDTO.importConfigurationId
    });

    this.configurationsService.updateConfigurationFromTemplate(updateConfiguration).subscribe((configDetailsDTO: ExportInterfaceConfigurationDetailsDTO) => {
      this.spinnerService.hide();
      this.loadConfigurationDetails(this.configurationDetailsDTO.id)
    });
  }

  //Forms stuff

  public loadFormsData() {
    this.resetForms();

    this.configurationFormGeneral.setValue({
      configurationName: this.configurationDetailsDTO.configurationName,
      type: this.configurationDetailsDTO.type,
      description: this.configurationDetailsDTO.description,
      transferType: this.configurationDetailsDTO.transferType,
      mode: this.configurationDetailsDTO.mode,
      activationEmailTemplateId: this.configurationDetailsDTO.activationEmailTemplateId,
      activationEmailLastSentOn: this.configurationDetailsDTO.activationEmailLastSentOn != null ? this.configurationDetailsDTO.activationEmailLastSentOn.toFormat("dd.MM.yyyy HH:mm:ss") : null,
      active: this.configurationDetailsDTO.active,
      userAccessList: this.configurationDetailsDTO.userAccessList ? this.configurationDetailsDTO.userAccessList : [],
    });
    if (this.configurationDetailsDTO.mode === ExportInterfaceConfigurationMode.Response) {
      this.configurationFormGeneral.controls.type.disable()
    } else {
      this.configurationFormGeneral.controls.type.enable()
    }

    if (this.configurationDetailsDTO.fileDetails?.fieldAssignments?.assignments?.length > 0) {
      this.configurationFormGeneral.get('type').disable();
    }
    else {
      this.configurationFormGeneral.get('type').enable();
    }

    this.configurationFormBacklog.setValue({
      dataSelectionType: this.configurationDetailsDTO.dataSelectionType,
      backlogFilterLocations: (this.configurationDetailsDTO.backlogFilterLocations != null && this.configurationDetailsDTO.backlogFilterLocations.length > 0) ? this.configurationDetailsDTO.backlogFilterLocations.split(',') : null
    });

    this.configurationFormFinance.setValue({
      schedule: this.configurationDetailsDTO.financeSchedule,
      sendEmptyData: this.configurationDetailsDTO.financeSendEmptyData ? true : false,
      dataSelectionType: this.configurationDetailsDTO.financeDataSelectionType
    });

    if (this.configurationDetailsDTO.scheduleDetails) {
      this.configurationFormTimeplan.setValue({
        startDate: this.configurationDetailsDTO.scheduleDetails.startDateTime.toJSDate(),
        hasEndDate: this.configurationDetailsDTO.scheduleDetails.endDateTime ? true : false,
        endDate: this.configurationDetailsDTO.scheduleDetails.endDateTime ? this.configurationDetailsDTO.scheduleDetails.endDateTime.toJSDate() : null,
        startTime: this.configurationDetailsDTO.scheduleDetails.startDateTime.toJSDate(),
        // min value for date is jan. 1, 1970
        endTime: DateTime.fromObject({year: 1970, month: 1, day: 1}).plus(this.configurationDetailsDTO.scheduleDetails.endTimeSpan).toJSDate(),
        repeatEveryNMinutes: this.configurationDetailsDTO.scheduleDetails.repeatEveryNMinutes,
        weekDays: this.configurationDetailsDTO.scheduleDetails.weekdays,
      });
    }

    if (this.configurationDetailsDTO.fTPClientDetails) {
      this.configurationFormFTP.setValue({
        url: this.configurationDetailsDTO.fTPClientDetails.uRL,
        port: this.configurationDetailsDTO.fTPClientDetails.port,
        clientType: this.configurationDetailsDTO.fTPClientDetails.clientType,
        userName: this.configurationDetailsDTO.fTPClientDetails.userName,
        password: this.configurationDetailsDTO.fTPClientDetails.password,
        relativePathOfFiles: this.configurationDetailsDTO.fTPClientDetails.relativePathOfFiles,
        useCertificate: this.configurationDetailsDTO.fTPClientDetails.useCertificate,
        certificateMediaId: this.configurationDetailsDTO.fTPClientDetails.certificateMediaId,
        certificateMediaName: this.configurationDetailsDTO.fTPClientDetails.certificateMediaName,
        certificateContentBase64: null,
        certificatePassword: this.configurationDetailsDTO.fTPClientDetails.certificatePassword,
        certificateMediaType: this.configurationDetailsDTO.fTPClientDetails.certificateMediaType
      });
    }

    if (this.configurationDetailsDTO.sFTPDetails) {
      this.configurationFormSFTP.setValue({
        useCustomFolderStructure: this.configurationDetailsDTO.sFTPDetails.useCustomFolderStructure
      });
    }

    if (this.configurationDetailsDTO.rESTDetails) {
      this.configurationFormREST.setValue({
        isBearerToken: this.configurationDetailsDTO.rESTDetails.authenticationType == ExportInterfaceConfigurationRESTAuthType.BearerToken ? true : false,
        clientId: this.configurationDetailsDTO.rESTDetails.bearerTokenDetails?.clientId,
        apiFixKey: this.configurationDetailsDTO.rESTDetails.aPIFixKeyDetails?.aPIFixKey,
        apiFixValue: this.configurationDetailsDTO.rESTDetails.aPIFixKeyDetails?.aPIFixKeyValue,
        clientSecret: this.configurationDetailsDTO.rESTDetails.bearerTokenDetails?.clientSecret,
        refreshTokenLifetime: this.configurationDetailsDTO.rESTDetails.bearerTokenDetails?.refreshTokeLifetimeInH,
      });

      //Set APIFixKeyValue if empty
      if (this.configurationDetailsDTO.rESTDetails.aPIFixKeyDetails.aPIFixKeyValue === '') {
        this.configurationFormREST.get('apiFixValue').setValue(PasswordGenerator.generatePassword());
      }

      //Set ClientSecret if empty
      if (this.configurationDetailsDTO.rESTDetails.aPIFixKeyDetails.aPIFixKeyValue === '') {
        this.configurationFormREST.get('clientSecret').setValue(PasswordGenerator.generatePassword());
      }
    }

    if (this.configurationDetailsDTO.emailDetails) {
      this.configurationFormEmail.setValue({
        senderName: this.configurationDetailsDTO.emailDetails.senderName,
        subject: this.configurationDetailsDTO.emailDetails.subject,
        textHTML: this.configurationDetailsDTO.emailDetails.textHTML,
        emailAccountId: this.configurationDetailsDTO.emailDetails.emailAccountId
      });
    }

    if (this.configurationDetailsDTO.fileDetails) {
      this.configurationFormFileName.setValue({
        fileFormat: this.configurationDetailsDTO.fileDetails.fileFormat,
        titleInFile: this.configurationDetailsDTO.fileDetails.titleInFile,
        noOfDecimalPlaces: this.configurationDetailsDTO.fileDetails.noOfDecimalPlaces,
        decimalSeperator: this.configurationDetailsDTO.fileDetails.decimalSeparator,
        currency: this.configurationDetailsDTO.fileDetails.currency,
        elementTag: this.configurationDetailsDTO.fileDetails.elementTag,
        xmlRootTag: this.configurationDetailsDTO.fileDetails.xmlRootTag,
        includeXmlDeclaration: this.configurationDetailsDTO.fileDetails.includeXmlDeclaration,
        seperator: this.configurationDetailsDTO.fileDetails.separator,
        textFormat: this.configurationDetailsDTO.fileDetails.textFormat,
        processType: this.configurationDetailsDTO.fileDetails.processType,
        fileExtension: this.configurationDetailsDTO.fileDetails.fileExtension,
        fileName: this.configurationDetailsDTO.fileDetails.fileName,
        csvEnQuoteValues: this.configurationDetailsDTO.fileDetails.csvEnQuoteValues,
        suppressHeaderLineInCsv: this.configurationDetailsDTO.fileDetails.suppressHeaderLineInCsv,
        generateEndLineInCsv: this.configurationDetailsDTO.fileDetails.generateEndLineInCsv
      });
      this.configurationFormFileFields.setValue({
        assignments: this.configurationDetailsDTO.fileDetails.fieldAssignments.assignments,
      });
      this.configurationFormFileLineBasedAllAttributes.setValue({
        removeEmptyAttributes: this.configurationDetailsDTO.fileDetails.lineBasedAllAttributes.removeEmptyAttributes,
        countryCode: this.configurationDetailsDTO.fileDetails.lineBasedAllAttributes.countryCode,
        useTitleColumn: this.configurationDetailsDTO.fileDetails.lineBasedAllAttributes.useTitleColumn,
        addTitleToAttributeDescription: this.configurationDetailsDTO.fileDetails.lineBasedAllAttributes.addTitleToAttributeDescription,
        titleAttributeSeparator: this.configurationDetailsDTO.fileDetails.lineBasedAllAttributes.titleAttributeSeparator,
        useUnitColumn: this.configurationDetailsDTO.fileDetails.lineBasedAllAttributes.useUnitColumn,
        addUnitToAttributeValue: this.configurationDetailsDTO.fileDetails.lineBasedAllAttributes.addUnitToAttributeValue,
        unitAttributeSeparator: this.configurationDetailsDTO.fileDetails.lineBasedAllAttributes.unitAttributeSeparator,
        itemNumberDenominator: this.configurationDetailsDTO.fileDetails.lineBasedAllAttributes.itemNumberDenominator,
        titleDenominator: this.configurationDetailsDTO.fileDetails.lineBasedAllAttributes.titleDenominator,
        attributeDescriptionDenominator: this.configurationDetailsDTO.fileDetails.lineBasedAllAttributes.attributeDescriptionDenominator,
        attributeValueDenominator: this.configurationDetailsDTO.fileDetails.lineBasedAllAttributes.attributeValueDenominator,
        unitDenominator: this.configurationDetailsDTO.fileDetails.lineBasedAllAttributes.unitDenominator,
        attributeTag: this.configurationDetailsDTO.fileDetails.lineBasedAllAttributes.attributeTag,
        sendLegacyNoAsItemNo: this.configurationDetailsDTO.fileDetails.lineBasedAllAttributes.sendLegacyNoAsItemNo,
        additionalColumns: this.configurationDetailsDTO.fileDetails.lineBasedAllAttributes.additionalColumns
      });
      this.configurationFormFileFilter.setValue({
        lowerPriceLimit: this.configurationDetailsDTO.fileDetails.filter.lowerPriceLimit,
        filterCustomerReference: this.configurationDetailsDTO.fileDetails.filter.filterCustomerReference,
        filterEAN: this.configurationDetailsDTO.fileDetails.filter.filterEAN,
        clearingItemFilter: this.configurationDetailsDTO.fileDetails.filter.clearingItemFilter,
        additionalStockLocation: this.configurationDetailsDTO.fileDetails.filter.additionalStockLocation,
        filterFixPrice: this.configurationDetailsDTO.fileDetails.filter.filterFixPrice,
        brandFilters: this.configurationDetailsDTO.fileDetails.filter.brandFilters,
        brandSubIncludeFilters: this.configurationDetailsDTO.fileDetails.filter.brandSubIncludeFilters,
      });
    }

    this.resetFormStatus();
    this.configurationDetailsDTOUpdated.next(this.configurationDetailsDTO);
  }

  public resetForms() {
    this.configurationFormGeneral.reset();
    this.configurationFormBacklog.reset();
    this.configurationFormFinance.reset();
    this.configurationFormTimeplan.reset();
    this.configurationFormFTP.reset();
    this.configurationFormSFTP.reset();
    this.configurationFormREST.reset();
    this.configurationFormEmail.reset();
    this.configurationFormFileName.reset();
    this.configurationFormFileFields.reset();
    this.configurationFormFileLineBasedAllAttributes.reset();
    this.configurationFormFileFilter.reset();

    this.resetFormStatus();
  }

  public isFormDirty(): boolean {
    return this.configurationFormGeneral.dirty ||
      this.configurationFormBacklog.dirty ||
      this.configurationFormFinance.dirty ||
      this.configurationFormTimeplan.dirty ||
      this.configurationFormFTP.dirty ||
      this.configurationFormSFTP.dirty ||
      this.configurationFormREST.dirty ||
      this.configurationFormEmail.dirty ||
      this.configurationFormFileName.dirty ||
      this.configurationFormFileFields.dirty ||
      this.configurationFormFileLineBasedAllAttributes.dirty ||
      this.configurationFormFileFilter.dirty;
  }

  public createConfigurationDetailsDTOFromForm(): ExportInterfaceConfigurationDetailsDTO {
    const generalDetailsValues = this.configurationFormGeneral.getRawValue();
    var detailsDTO = new ExportInterfaceConfigurationDetailsDTO({
      id: this.configurationDetailsDTO.id,
      clientId: this.configurationDetailsDTO.clientId,
      configurationName: generalDetailsValues.configurationName,
      type: generalDetailsValues.type,
      transferType: generalDetailsValues.transferType,
      mode: generalDetailsValues.mode,
      dataSelectionType: ExportInterfaceConfigurationDataSelectionType.CustomerNumber,
      backlogFilterLocations: undefined,
      activationEmailTemplateId: generalDetailsValues.activationEmailTemplateId,
      activationEmailLastSentOn: this.configurationDetailsDTO.activationEmailLastSentOn,
      description: generalDetailsValues.description,
      active: generalDetailsValues.active,
      userAccessList: (generalDetailsValues.userAccessList as ExportInterfaceConfigurationUserAccessInfoDTO[])?.filter((user) => user.hasAccess),
      scheduleDetails: this.createTimeScheduleDTOFromForm(),
      emailDetails: this.createEmailDetailsDTOFromForm(),
      fTPClientDetails: this.createFTPClientDTOFromForm(),
      sFTPDetails: this.createSFTPDTOFromForm(),
      rESTDetails: this.createRestDetailsDTOFromForm(),
      fileDetails: this.createFileDetailsDTOFromForm(),
      financeDataSelectionType: ExportInterfaceConfigurationFinanceDataSelectionType.CustomerNumber,
      financeSchedule: ExportInterfaceConfigurationFinanceSchedule.PreviousDay,
      financeSendEmptyData: false
    });
    const backlogDetails = this.configurationFormBacklog.getRawValue();
    const backlogFilterLocations = backlogDetails.backlogFilterLocations?.toString()
    detailsDTO.dataSelectionType = backlogDetails.dataSelectionType;
    detailsDTO.backlogFilterLocations = backlogFilterLocations?.length !== 0 ? backlogFilterLocations : null;

    const financeDetails = this.configurationFormFinance.getRawValue();
    detailsDTO.financeDataSelectionType = financeDetails.dataSelectionType;
    detailsDTO.financeSchedule = financeDetails.schedule;
    detailsDTO.financeSendEmptyData = financeDetails.sendEmptyData;
    return detailsDTO;
  }

  public createTimeScheduleDTOFromForm(): ExportInterfaceConfigurationDetailsTimeScheduleDTO {
    const scheduleDetailsValues = this.configurationFormTimeplan.getRawValue();
    var startTime = scheduleDetailsValues.startTime as Date;
    var startDateTime = DateTime.fromJSDate(scheduleDetailsValues.startDate).startOf('day').plus({ hours: startTime.getHours(), minutes: startTime.getMinutes() }).toUTC();
    var endTimeSpan = Duration.fromObject({ hours: scheduleDetailsValues.endTime.getHours(), minutes: scheduleDetailsValues.endTime.getMinutes() });
    return new ExportInterfaceConfigurationDetailsTimeScheduleDTO({
      startDateTime,
      endDateTime: scheduleDetailsValues.endDate ? DateTime.fromJSDate(scheduleDetailsValues.endDate).startOf('day').plus(endTimeSpan).toUTC() : null,
      endTimeSpan,
      repeatEveryNMinutes: scheduleDetailsValues.repeatEveryNMinutes,
      weekdays: scheduleDetailsValues.weekDays,
    });
  }

  public createFTPClientDTOFromForm(): ExportInterfaceConfigurationDetailsFTPClientDTO {
    const ftpClientDetailsValues = this.configurationFormFTP.getRawValue();
    return new ExportInterfaceConfigurationDetailsFTPClientDTO({
      uRL: ftpClientDetailsValues.url,
      port: ftpClientDetailsValues.port,
      clientType: ftpClientDetailsValues.clientType,
      userName: ftpClientDetailsValues.userName,
      password: ftpClientDetailsValues.password,
      relativePathOfFiles: ftpClientDetailsValues.relativePathOfFiles,
      useCertificate: ftpClientDetailsValues.useCertificate,
      certificateMediaId: ftpClientDetailsValues.certificateMediaId,
      certificateMediaName: ftpClientDetailsValues.certificateMediaName,
      certificateContentBase64: ftpClientDetailsValues.certificateContentBase64,
      certificatePassword: ftpClientDetailsValues.certificatePassword,
      certificateMediaType: ftpClientDetailsValues.certificateMediaType
    });
  }

  public createSFTPDTOFromForm(): ExportInterfaceConfigurationDetailsSFTPDTO {
    const sftpDetailsValues = this.configurationFormSFTP.getRawValue();
    return new ExportInterfaceConfigurationDetailsSFTPDTO({
      useCustomFolderStructure: sftpDetailsValues.useCustomFolderStructure
    });
  }

  public createEmailDetailsDTOFromForm(): ExportInterfaceConfigurationDetailsEmailDTO {
    const emailDetailsValues = this.configurationFormEmail.getRawValue();
    return new ExportInterfaceConfigurationDetailsEmailDTO({
      senderName: emailDetailsValues.senderName,
      subject: emailDetailsValues.subject,
      textHTML: emailDetailsValues.textHTML,
      emailAccountId: emailDetailsValues.emailAccountId
    });
  }

  public createRestDetailsDTOFromForm(): ExportInterfaceConfigurationDetailsRESTDTO {
    const restDetailsValues = this.configurationFormREST.getRawValue();
    return new ExportInterfaceConfigurationDetailsRESTDTO({
      authenticationType: restDetailsValues.isBearerToken ? ExportInterfaceConfigurationRESTAuthType.BearerToken : ExportInterfaceConfigurationRESTAuthType.APIKey,
      bearerTokenDetails: new ExportInterfaceConfigurationDetailsRESTBTDTO({
        clientId: restDetailsValues.clientId,
        clientSecret: restDetailsValues.clientSecret,
        refreshTokeLifetimeInH: restDetailsValues.refreshTokenLifetime,
      }),
      aPIFixKeyDetails: new ExportInterfaceConfigurationDetailsRESTAPIDTO({
        aPIFixKey: restDetailsValues.apiFixKey,
        aPIFixKeyValue: restDetailsValues.apiFixValue,
      }),
    });
  }

  public createFileDetailsDTOFromForm(): ExportInterfaceConfigurationDetailsFileDTO {
    const fileNameValues = this.configurationFormFileName.getRawValue();
    const fileFieldsValues = this.configurationFormFileFields.getRawValue();
    const lineBasedAllAttributesValues = this.configurationFormFileLineBasedAllAttributes.getRawValue();
    const fileFilterValues = this.configurationFormFileFilter.getRawValue();

    //Assign arrayindex to positions
    let fieldAssignments: ExportInterfaceFieldAssignmentDTO[] = fileFieldsValues.assignments?.map((a: IExportInterfaceFieldAssignmentDTO) => new ExportInterfaceFieldAssignmentDTO(a))
    for (let i = 0; i < fieldAssignments.length; i++) {
      fieldAssignments[i].position = i;
    }

    return new ExportInterfaceConfigurationDetailsFileDTO({
      fileFormat: fileNameValues.fileFormat,
      titleInFile: fileNameValues.titleInFile,
      noOfDecimalPlaces: fileNameValues.noOfDecimalPlaces,
      decimalSeparator: fileNameValues.decimalSeperator,
      currency: fileNameValues.currency,
      elementTag: fileNameValues.elementTag,
      includeXmlDeclaration: fileNameValues.includeXmlDeclaration,
      xmlRootTag: fileNameValues.xmlRootTag,
      separator: fileNameValues.seperator,
      textFormat: fileNameValues.textFormat,
      fileExtension: fileNameValues.fileExtension,
      csvEnQuoteValues: fileNameValues.csvEnQuoteValues,
      suppressHeaderLineInCsv: fileNameValues.suppressHeaderLineInCsv,
      generateEndLineInCsv: fileNameValues.generateEndLineInCsv,
      fileName: fileNameValues.fileName,
      processType: fileNameValues.processType,
      fieldAssignments: new ExportInterfaceFieldAssignmentsDTO({
        assignments: fieldAssignments,
      }),
      filter: new ExportInterfaceConfigurationDetailsFileFilterDTO({
        lowerPriceLimit: fileFilterValues.lowerPriceLimit,
        filterCustomerReference: fileFilterValues.filterCustomerReference,
        filterEAN: fileFilterValues.filterEAN,
        clearingItemFilter: fileFilterValues.clearingItemFilter,
        additionalStockLocation: fileFilterValues.additionalStockLocation,
        filterFixPrice: fileFilterValues.filterFixPrice,
        brandFilters: fileFilterValues.brandFilters?.map((f: IExportInterfaceConfigurationDetailsFileFilterBrandSubDTO) => new ExportInterfaceConfigurationDetailsFileFilterBrandSubDTO(f)),
        brandSubIncludeFilters: fileFilterValues.brandSubIncludeFilters?.map((f: IExportInterfaceConfigurationDetailsFileFilterBrandSubDTO) => new ExportInterfaceConfigurationDetailsFileFilterBrandSubDTO(f)),
      }),
      lineBasedAllAttributes: new ExportInterfaceConfigurationDetailsFileLineBasedAllAttributesDTO({
        removeEmptyAttributes: lineBasedAllAttributesValues.removeEmptyAttributes,
        countryCode: lineBasedAllAttributesValues.countryCode,
        useTitleColumn: lineBasedAllAttributesValues.useTitleColumn,
        addTitleToAttributeDescription: lineBasedAllAttributesValues.addTitleToAttributeDescription,
        titleAttributeSeparator: lineBasedAllAttributesValues.titleAttributeSeparator,
        useUnitColumn: lineBasedAllAttributesValues.useUnitColumn,
        addUnitToAttributeValue: lineBasedAllAttributesValues.addUnitToAttributeValue,
        unitAttributeSeparator: lineBasedAllAttributesValues.unitAttributeSeparator,
        itemNumberDenominator: lineBasedAllAttributesValues.itemNumberDenominator,
        titleDenominator: lineBasedAllAttributesValues.titleDenominator,
        attributeDescriptionDenominator: lineBasedAllAttributesValues.attributeDescriptionDenominator,
        attributeValueDenominator: lineBasedAllAttributesValues.attributeValueDenominator,
        unitDenominator: lineBasedAllAttributesValues.unitDenominator,
        attributeTag: lineBasedAllAttributesValues.attributeTag,
        sendLegacyNoAsItemNo : lineBasedAllAttributesValues.sendLegacyNoAsItemNo,
        additionalColumns: lineBasedAllAttributesValues.additionalColumns
      })
    });
  }

  private initializeForms() {
    this.configurationFormGeneral = this.fb.group({
      configurationName: ['New Configuration', Validators.required],
      type: [0, Validators.required],
      activationEmailTemplateId: [null],
      activationEmailLastSentOn: [{ value: null, disabled: true }],
      description: [''],
      transferType: [0, Validators.required],
      mode: [0, Validators.required],
      active: [true],
      userAccessList: [],
    });

    this.configurationFormBacklog = this.fb.group({
      dataSelectionType: [ExportInterfaceConfigurationDataSelectionType.CustomerNumber, Validators.required],
      backlogFilterLocations: [null, Validators.pattern(/[\S]/)]
    });

    this.configurationFormFinance = this.fb.group({
      dataSelectionType: [ExportInterfaceConfigurationFinanceDataSelectionType.CustomerNumber, Validators.required],
      schedule: [ExportInterfaceConfigurationFinanceSchedule.PreviousDay, Validators.required],
      sendEmptyData: [false]
    });

    this.configurationFormTimeplan = this.fb.group({
      startDate: [null, Validators.required],
      hasEndDate: [false, Validators.required],
      endDate: [null],
      startTime: [null, Validators.required],
      endTime: [null, Validators.required],
      repeatEveryNMinutes: [1440, Validators.required],
      weekDays: [0, ConfigurationFormTimeplanValidators.dayPickerValueValidation],
    }, { validators: Validators.compose([ConfigurationFormTimeplanValidators.checkStartAndEndTime, ConfigurationFormTimeplanValidators.checkStartAndEndDate]) });

    this.configurationFormREST = this.fb.group({
      isBearerToken: [true],
      clientId: [null],
      apiFixKey: [null],
      apiFixValue: [null],
      clientSecret: [null],
      refreshTokenLifetime: [8],
    });

    this.configurationFormFileName = this.fb.group({
      fileFormat: [1, Validators.required],
      titleInFile: [''],
      noOfDecimalPlaces: [2, Validators.required],
      decimalSeperator: [1, Validators.required],
      currency: [0, Validators.required],
      elementTag: ['Item', Validators.required],
      xmlRootTag: [null],
      includeXmlDeclaration: [null],
      seperator: [1, Validators.required],
      textFormat: [0, Validators.required],
      processType: [ExportInterfaceConfigurationFileProcessType.ColumnBased, Validators.required],
      fileName: ['', Validators.required],
      fileExtension: [null],
      csvEnQuoteValues: [false],
      suppressHeaderLineInCsv: [false],
      generateEndLineInCsv: [false]
    });

    this.configurationFormFileFields = this.fb.group({
      assignments: [[]],
    });

    this.configurationFormFileLineBasedAllAttributes = this.fb.group({
      removeEmptyAttributes: [false, Validators.required],
      countryCode: [null, Validators.required],
      useTitleColumn: [false, Validators.required],
      addTitleToAttributeDescription: [false, Validators.required],
      titleAttributeSeparator: null,
      useUnitColumn: [false, Validators.required],
      addUnitToAttributeValue: [false, Validators.required],
      unitAttributeSeparator: null,
      itemNumberDenominator: null,
      titleDenominator: null,
      attributeDescriptionDenominator: null,
      attributeValueDenominator: null,
      unitDenominator: null,
      attributeTag: null,
      sendLegacyNoAsItemNo: [false],
      additionalColumns: null,
    });

    this.configurationFormFileFilter = this.fb.group({
      lowerPriceLimit: [''],
      filterCustomerReference: [false, Validators.required],
      filterEAN: [false, Validators.required],
      clearingItemFilter: [ExportInterfaceConfigurationClearingItemFilter.NoClearingItems],
      additionalStockLocation: [ExportInterfaceConfigurationAdditionalStockLocation.None],
      filterFixPrice: [false, Validators.required],
      brandFilters: [[]],
      brandSubIncludeFilters: [[]],
    });

    this.configurationFormFTP = this.fb.group({
      url: ['', Validators.required],
      port: [22, Validators.required],
      clientType: [2, Validators.required],
      userName: [null, [Validators.required]],
      password: [null, [Validators.required]],
      relativePathOfFiles: ['/', Validators.required],
      useCertificate: [false, Validators.required],
      certificateMediaId: [''],
      certificateMediaName: [''],
      certificateContentBase64: [null],
      certificatePassword: [null],
      certificateMediaType: [null]
    });

    this.configurationFormSFTP = this.fb.group({
      useCustomFolderStructure: [false]
    });

    this.configurationFormEmail = this.fb.group({
      senderName: ['Purchase Team', Validators.required],
      subject: ['', Validators.required],
      textHTML: ['', Validators.required],
      emailAccountId: [null, Validators.required]
    });

    this._subscription.add(this.configurationFormGeneral.get('transferType').valueChanges
      .subscribe((value) => {
        this.transferType = value;
        this.transferTypeChanged.next(this.transferType);
      }),
    );

    this._subscription.add(this.configurationFormGeneral.get('type').valueChanges
      .subscribe((value) => {
        if (value != null) {
          this.configurationsService.configurationType = value;
          this.configurationTemplateService.configurationType = value;
          this.configurationType = value;
          this.typeChanged.next(value);
        }
      }),
    );

    this._subscription.add(this.configurationFormFileName.get('processType').valueChanges
      .subscribe((value) => {
        this.fileProcessType = value;
        this.fileProcessTypeChanged.next(this.fileProcessType);
      })
    );

    this._subscription.add(this.configurationFormFileLineBasedAllAttributes.get('addTitleToAttributeDescription').valueChanges
      .subscribe((value) => {
        if (value) {
          this.configurationFormFileLineBasedAllAttributes.get('titleAttributeSeparator').enable({ onlySelf: true });
        } else {
          this.configurationFormFileLineBasedAllAttributes.get('titleAttributeSeparator').disable({ onlySelf: true });
        }
      })
    );

    this._subscription.add(this.configurationFormFileLineBasedAllAttributes.get('addUnitToAttributeValue').valueChanges
      .subscribe((value) => {
        if (value) {
          this.configurationFormFileLineBasedAllAttributes.get('unitAttributeSeparator').enable({ onlySelf: true });
        } else {
          this.configurationFormFileLineBasedAllAttributes.get('unitAttributeSeparator').disable({ onlySelf: true });
        }
      })
    );

    this._subscription.add(this.configurationFormFileName.get('fileFormat').valueChanges
      .subscribe((value: ExportInterfaceConfigurationFileFormat) => {
        if (value === ExportInterfaceConfigurationFileFormat.XML) {
          this.configurationFormFileLineBasedAllAttributes.get('attributeTag').enable({ onlySelf: true });
        } else {
          this.configurationFormFileLineBasedAllAttributes.get('attributeTag').disable({ onlySelf: true });
        }
      })
    );
  }

  private resetFormStatus(): void {
    this.configurationFormGeneral.markAsPristine();
    this.configurationFormBacklog.markAsPristine();
    this.configurationFormFinance.markAsPristine();
    this.configurationFormTimeplan.markAsPristine();
    this.configurationFormFTP.markAsPristine();
    this.configurationFormSFTP.markAsPristine();
    this.configurationFormREST.markAsPristine();
    this.configurationFormEmail.markAsPristine();
    this.configurationFormFileName.markAsPristine();
    this.configurationFormFileFields.markAsPristine();
    this.configurationFormFileLineBasedAllAttributes.markAsPristine();
    this.configurationFormFileFilter.markAsPristine();
  }
}
